/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数で、独立した component subtree を持たないため
 * lint 警告の前提 (subtree 破棄) に該当しない。
 */
import { useTranslation } from 'react-i18next'

import { Badge, HStack, Text } from '@enechain/ecloud-designsystem'

import {
  AuditCell,
  MasterDataTable,
  type MasterDataTableColumn,
  standardRowActions,
} from '~/components/common/MasterDataTable'
import { sortableColumn } from '~/components/common/SortableColumnHeader'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { convertAreaLabel } from '~/models/area'
import type { SortOrder } from '~/models/tableSort'
import {
  type GenerationSiteRow,
  type SiteSort,
  type SiteSortKey,
  nextSiteSort,
} from '~/pages/AdminPlantUnitMaster/models/siteRows'

type Props = {
  rows: GenerationSiteRow[]
  /** 今の並び (列見出しを押すまでは undefined = 既定順)。 */
  sort: SiteSort | undefined
  onSortChange: (sort: SiteSort) => void
  isLoading: boolean
  onEdit: (row: GenerationSiteRow) => void
  onDelete: (row: GenerationSiteRow) => void
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

const NO_VALUE = '—'

/** 発電所 (サイト) の一覧表。備考は 1 行に省略し、全文は hover で見せる。 */
export const GenerationSiteTable = ({
  rows,
  sort,
  onSortChange,
  isLoading,
  onEdit,
  onDelete,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const resolveDisplayName = useMemberDisplayName()

  const sortableOf = (
    key: SiteSortKey,
    label: string,
  ): ReturnType<typeof sortableColumn> =>
    sortableColumn({
      order: sort?.key === key ? (sort.order satisfies SortOrder) : undefined,
      label,
      nextOrder: nextSiteSort(sort, key).order,
      onClick: () => onSortChange(nextSiteSort(sort, key)),
    })

  // エリア列: 配下に、系統コードのエリアが発電所と違うユニットがあれば、同じセルの中に印を添える
  const areaCell = (row: GenerationSiteRow): React.ReactNode => {
    const items = row.areaMismatchedUnits
    if (items.length === 0) {
      return convertAreaLabel(row.areaCode)
    }
    return (
      <HStack gap={1}>
        <Text as="span" whiteSpace="nowrap">
          {convertAreaLabel(row.areaCode)}
        </Text>
        <Badge
          whiteSpace="nowrap"
          status="warning"
          title={items
            .map((item) =>
              t('page:adminPlantUnitMaster.site.areaMismatchUnit', {
                unit: item.unitName,
                plantCode: item.plantCode,
                plantArea: convertAreaLabel(item.plantAreaCode),
              }),
            )
            .join('\n')}
        >
          {t('page:adminPlantUnitMaster.unit.missing.areaMismatch')}
        </Badge>
      </HStack>
    )
  }

  const columns: Array<MasterDataTableColumn<GenerationSiteRow>> = [
    {
      key: 'siteName',
      ...sortableOf(
        'siteName',
        t('page:adminPlantUnitMaster.site.field.siteName'),
      ),
      cell: (row) => row.siteName,
    },
    {
      key: 'areaCode',
      ...sortableOf(
        'areaCode',
        t('page:adminPlantUnitMaster.site.field.areaCode'),
      ),
      cell: areaCell,
    },
    {
      key: 'unitCount',
      ...sortableOf(
        'unitCount',
        t('page:adminPlantUnitMaster.site.field.unitCount'),
      ),
      format: 'number',
      cell: (row) => row.unitCount,
    },
    {
      key: 'note',
      header: t('page:adminPlantUnitMaster.site.field.note'),
      cell: (row) =>
        row.note === '' ? (
          NO_VALUE
        ) : (
          <Text
            as="span"
            display="block"
            overflow="hidden"
            maxWidth="360px"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            title={row.note}
          >
            {row.note}
          </Text>
        ),
    },
    {
      key: 'updatedAt',
      ...sortableOf('updatedAt', t('common:audit.updatedAtHeader')),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={undefined} />
      ),
    },
    {
      key: 'updatedBy',
      header: t('common:audit.updatedByHeader'),
      nowrap: true,
      cell: (row) => resolveDisplayName(row.updatedBy) ?? NO_VALUE,
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={t('page:adminPlantUnitMaster.site.empty')}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onEdit}
      renderRowActions={standardRowActions(onEdit, onDelete)}
      rows={rows}
    />
  )
}
